/**
 * Liten sparkline over lesninger de siste N dagene.
 *
 * Leser `views_by_day` rett fra artikkeldokumentet — kartet skrives av
 * recordArticleViewInternal, så ingen egen spørring eller snapshot-jobb.
 * Dager uten lesninger mangler i kartet og fylles inn som 0, ellers ville
 * grafen komprimert tid og løyet om trenden.
 */
import React from 'react';

interface Props {
  viewsByDay?: Record<string, number>;
  days?: number;
  className?: string;
}

/** Fyller ut manglende dager med 0 og returnerer verdiene i kronologisk rekkefølge. */
export function toDailySeries(viewsByDay: Record<string, number> | undefined, days: number, endYmd: string): number[] {
  const end = Date.parse(`${endYmd}T00:00:00Z`);
  const out: number[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(end - i * 86400000).toISOString().slice(0, 10);
    out.push(Number(viewsByDay?.[d] ?? 0));
  }
  return out;
}

const ViewSparkline: React.FC<Props> = ({ viewsByDay, days = 14, className }) => {
  // MÅ være Oslo-dato: recordArticleViewInternal skriver nøkkelen med osloYmd.
  // Med UTC her ville de to divergert etter 22:00 norsk tid, og dagens lesninger
  // havnet i en bøtte grafen ikke leser.
  const endYmd = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Oslo', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
  const series = React.useMemo(() => toDailySeries(viewsByDay, days, endYmd), [viewsByDay, days, endYmd]);
  const total = series.reduce((a, b) => a + b, 0);

  // Ingen data → ingen graf. En flat strek på null ser ut som et måleproblem.
  if (total === 0) return null;

  const max = Math.max(...series, 1);
  const w = 64;
  const h = 16;
  const step = w / Math.max(series.length - 1, 1);
  const points = series
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`)
    .join(' ');

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className={className}
      role="img"
      aria-label={`${total} lesninger siste ${days} dager`}
      preserveAspectRatio="none"
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

export default ViewSparkline;
